import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { getIcon } from "@/lib/icons";
import {
  BookOpen, Heart, Star, Award, GraduationCap,
  CheckCircle, ArrowRight, ChevronLeft, ChevronRight, MessageCircle,
} from "lucide-react";

const BANNER_1 = "https://customer-assets.emergentagent.com/job_tozan-modern/artifacts/27cqqr1d_Banner%201.jpg";
const BANNER_2 = "https://customer-assets.emergentagent.com/job_tozan-modern/artifacts/qln27wk3_Banner%202.jpg";
const BANNER_3 = "https://customer-assets.emergentagent.com/job_tozan-modern/artifacts/rclk3894_Banner%203.jpg";
const LOGO     = "https://customer-assets.emergentagent.com/job_tozan-modern/artifacts/febczm58_Logo%20Website.png";
const FALLBACK_BANNERS = [BANNER_1, BANNER_2, BANNER_3];

const FALLBACK_STATS = [
  { number: "50+",  label: "Siswa Terdaftar", icon: "Users" },
  { number: "12",   label: "Tenaga Pengajar",  icon: "GraduationCap" },
  { number: "3",    label: "Program Unggulan", icon: "Star" },
  { number: "2019", label: "Tahun Berdiri",    icon: "Award" },
];

const features = [
  { title: "Tahfidz Al-Qur'an",                    desc: "Program menghafal Al-Qur'an harian dengan metode yang terstruktur dan menyenangkan.",               icon: BookOpen },
  { title: "Berlandaskan Ahlussunnah wal Jamaah",  desc: "Pengajaran akidah dan amaliyah berbasis Al-Qur'an, Hadits, serta tradisi keilmuan ulama salaf.",  icon: Star },
  { title: "Pendidikan Berkarakter",               desc: "Menanamkan adab dan akhlak mulia menjadi fondasi seluruh kegiatan pembelajaran.",                 icon: Heart },
  { title: "Tenaga Pengajar Profesional",          desc: "Guru berdedikasi dengan latar belakang akademik yang kuat dalam pendidikan Islam.",               icon: GraduationCap },
  { title: "Kurikulum Terintegrasi",               desc: "Memadukan kurikulum nasional dengan nilai-nilai kepesantrenan secara seimbang.",                  icon: Award },
  { title: "Biaya Terjangkau & Wakaf",             desc: "Subsidi pewakaf meringankan biaya pendidikan. Anak yatim mendapat pendidikan gratis.",            icon: CheckCircle },
];

export default function Beranda() {
  const { settings, waUrl } = useSettings();
  const [current, setCurrent]   = useState(0);
  const [articles, setArticles] = useState([]);

  const banners    = settings?.banners?.length ? settings.banners.map((b) => b.image) : FALLBACK_BANNERS;
  const stats      = settings?.stats?.length   ? settings.stats   : FALLBACK_STATS;
  const heroLogo   = settings?.hero_logo       || LOGO;

  // Hero text dari settings
  const heroBadge          = settings?.hero_badge           || "AHLUSUNNAH WAL JAMAAH";
  const heroArabic         = settings?.hero_arabic          || "\u0628\u0650\u0633\u0652\u0645\u0650 \u0627\u0644\u0644\u0647\u0650 \u0627\u0644\u0631\u0651\u064e\u062d\u0652\u0645\u064f\u0627\u0646\u0650 \u0627\u0644\u0631\u0651\u064e\u062d\u0650\u064a\u0652\u0645\u0650";
  const heroTitle          = settings?.hero_title           || "Yayasan Wakaf";
  const heroTitleHighlight = settings?.hero_title_highlight || "Tongku Azhar";
  const heroTitleSuffix    = settings?.hero_title_suffix    || "Nahdliyyin";
  const heroDesc           = settings?.hero_desc            || "Mendidik dan mencetak generasi muda Islam menjadi ibadillah as-sholihin \u2014 hamba-hamba Allah yang saleh, berintegritas tinggi, dan berwawasan luas.";
  const heroBtn1Text       = settings?.hero_btn1_text       || "Daftar Sekarang";
  const heroBtn1Link       = settings?.hero_btn1_link       || "/informasi-pendaftaran";
  const heroBtn2Text       = settings?.hero_btn2_text       || "Donasi Sekarang";

  // Quote dari settings
  const quoteArabic      = settings?.quote_arabic      || "\u0637\u064e\u0644\u064e\u0628\u064f \u0627\u0644\u0652\u0639\u0650\u0644\u0652\u0645\u0650 \u0641\u064e\u0631\u0650\u064a\u0636\u064e\u0629\u064c";
  const quoteTranslation = settings?.quote_translation || "\"Menuntut ilmu adalah kewajiban bagi setiap Muslim.\"";
  const quoteSource      = settings?.quote_source      || "\u2014 Hadits Riwayat Ibnu Majah";

  // Auto-scroll banner slider
  useEffect(() => {
    if (banners.length <= 1) return undefined;
    const timer = setInterval(() => setCurrent((p) => (p + 1) % banners.length), 5000);
    return () => clearInterval(timer);
  }, [banners.length]);

  useEffect(() => { if (current >= banners.length) setCurrent(0); }, [banners.length, current]);

  useEffect(() => {
    api.get("/articles", { params: { published_only: true } })
      .then((res) => setArticles(res.data.slice(0, 4)))
      .catch(() => setArticles([]));
  }, []);

  return (
    <div style={{ paddingTop: 0 }}>

      {/* ===== HERO — Geometri Background ===== */}
      <section
        data-testid="hero-section"
        className="relative overflow-hidden flex items-center"
        style={{
          backgroundColor: "#044712",
          backgroundImage: "url('https://yayasanwakaftozan.org/res/geometri_background.png')",
          backgroundSize: "1280px 960px",
          backgroundRepeat: "repeat",
          paddingTop: "80px",
          paddingBottom: "48px",
        }}
      >
        {/* Gradient overlay */}
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(135deg, rgba(7,26,13,0.82) 0%, rgba(15,50,20,0.75) 55%, rgba(26,100,46,0.55) 100%)" }}
        />

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 md:px-10 w-full flex flex-col lg:flex-row items-center gap-5 lg:gap-10">

          {/* Left: Text */}
          <div className="flex-1 text-white w-full max-w-2xl">
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold tracking-widest mb-3"
              style={{ border: "1px solid rgba(212,175,55,0.5)", color: "#d4af37", backgroundColor: "rgba(212,175,55,0.08)" }}
            >
              <span>&#10022;</span> {heroBadge}
            </div>

            <p
              className="mb-2 leading-relaxed"
              style={{ fontFamily: "'Amiri', serif", color: "#d4af37", fontSize: "clamp(1.3rem, 4vw, 2.2rem)" }}
            >
              {heroArabic}
            </p>

            <h1
              className="font-bold leading-tight mb-3"
              style={{ fontSize: "clamp(1.5rem, 4vw, 3.2rem)", fontFamily: "'Poppins', sans-serif" }}
            >
              {heroTitle} <span style={{ color: "#d4af37" }}>{heroTitleHighlight}</span> {heroTitleSuffix}
            </h1>

            <p className="mb-5 leading-relaxed text-sm sm:text-base" style={{ color: "#a7d4b3" }}>
              {heroDesc}
            </p>

            <div className="flex flex-col sm:flex-row gap-3 mb-3">
              <Link
                to={heroBtn1Link}
                data-testid="hero-daftar-btn"
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-bold text-sm transition-all hover:scale-105 hover:shadow-xl active:scale-95"
                style={{ backgroundColor: "#d4af37", color: "#044712" }}
              >
                {heroBtn1Text} <ArrowRight size={16} />
              </Link>
            </div>

            <Link to="/tentang-kami" className="inline-flex items-center gap-1.5 text-xs hover:underline" style={{ color: "#86efac" }}>
              Pelajari Lebih Lanjut <ArrowRight size={13} />
            </Link>
          </div>

          {/* Right: Logo (desktop only) */}
          <div className="hidden lg:flex flex-1 items-center justify-center">
            <div className="relative animate-float">
              <div className="absolute inset-0 rounded-full blur-3xl" style={{ backgroundColor: "rgba(212,175,55,0.2)", transform: "scale(1.2)" }} />
              <img src={heroLogo} alt="Logo Yayasan Tozan" className="relative z-10 drop-shadow-2xl" style={{ width: "clamp(200px, 28vw, 360px)", height: "auto" }} />
            </div>
          </div>
        </div>
      </section>

      {/* ===== BANNER SLIDER (full-width, di bawah hero) ===== */}
      <div className="w-full overflow-hidden relative" style={{ marginTop: "3rem", marginBottom: "3rem" }} data-testid="banner-slider">
        {/* Slide images — full width, natural aspect ratio */}
        <div className="relative" style={{ aspectRatio: "3000 / 1407" }}>
          {banners.map((banner, idx) => (
            <div
              key={idx}
              className="absolute inset-0 transition-opacity duration-700"
              style={{ opacity: idx === current ? 1 : 0 }}
            >
              <img
                src={banner}
                alt={`Banner ${idx + 1}`}
                className="w-full h-full object-cover"
                loading={idx === 0 ? "eager" : "lazy"}
              />
            </div>
          ))}
        </div>

        {/* Dots */}
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2 z-10">
          {banners.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrent(idx)}
              aria-label={`Banner ${idx + 1}`}
              className="rounded-full transition-all"
              style={{
                width: idx === current ? "24px" : "8px",
                height: "8px",
                backgroundColor: idx === current ? "#d4af37" : "rgba(255,255,255,0.7)",
              }}
            />
          ))}
        </div>

        {/* Arrows (desktop) */}
        <button
          onClick={() => setCurrent((c) => (c - 1 + banners.length) % banners.length)}
          className="hidden md:flex absolute left-3 top-1/2 -translate-y-1/2 p-2 rounded-full z-10"
          style={{ backgroundColor: "rgba(0,0,0,0.4)", color: "white" }}
          aria-label="Sebelumnya"
        >
          <ChevronLeft size={20} />
        </button>
        <button
          onClick={() => setCurrent((c) => (c + 1) % banners.length)}
          className="hidden md:flex absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-full z-10"
          style={{ backgroundColor: "rgba(0,0,0,0.4)", color: "white" }}
          aria-label="Berikutnya"
        >
          <ChevronRight size={20} />
        </button>
      </div>

      {/* ===== STATS ===== */}
      <section data-testid="stats-section" style={{ backgroundColor: "#198530" }} className="py-7 sm:py-10">
        <div className="max-w-5xl mx-auto px-4 grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6 text-center text-white">
          {stats.map((stat, idx) => {
            const Icon = getIcon(stat.icon);
            return (
              <div key={idx} data-testid={`stat-${idx}`} className="flex flex-col items-center gap-1">
                <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-xl flex items-center justify-center mb-1.5"
                  style={{ backgroundColor: "rgba(212,175,55,0.15)", border: "1px solid rgba(212,175,55,0.3)" }}>
                  <Icon size={18} style={{ color: "#d4af37" }} />
                </div>
                <span className="text-xl sm:text-3xl font-bold" style={{ fontFamily: "'Poppins', sans-serif" }}>{stat.number}</span>
                <span className="text-xs" style={{ color: "#bbf7d0" }}>{stat.label}</span>
              </div>
            );
          })}
        </div>
      </section>

      {/* ===== KEUNGGULAN ===== */}
      <section data-testid="why-section" className="py-8 sm:py-12" style={{ backgroundColor: "#F8FAF8" }}>
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-6 sm:mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold tracking-widest mb-2"
              style={{ backgroundColor: "#eef9f0", color: "#198530", border: "1px solid #bbf7d0" }}>
              &#10022; KEUNGGULAN KAMI
            </div>
            <h2 className="text-xl sm:text-3xl font-bold text-gray-900 mb-1">
              Mengapa Memilih <span style={{ color: "#198530" }}>Yayasan Tozan?</span>
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto text-xs sm:text-sm">
              Memadukan keilmuan klasik pesantren dengan pendekatan modern.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {features.map((feat, idx) => (
              <div key={idx}
                className="bg-white rounded-xl p-4 transition-all duration-300"
                style={{ border: "1px solid #e5f0e8" }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = "0 10px 24px rgba(26,122,46,0.1)"; e.currentTarget.style.borderColor = "#86efac"; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.borderColor = "#e5f0e8"; }}
              >
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: "#eef9f0" }}>
                    <feat.icon size={17} style={{ color: "#198530" }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="h-0.5 w-8 rounded-full mb-1.5" style={{ backgroundColor: "#d4af37" }} />
                    <h3 className="font-bold text-gray-900 mb-1 text-xs sm:text-sm">{feat.title}</h3>
                    <p className="text-gray-500 text-xs leading-relaxed">{feat.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== KUTIPAN ISLAMI ===== */}
      <section className="py-8 sm:py-12 relative overflow-hidden" style={{ backgroundColor: "#044712" }}>
        <div className="absolute inset-0" style={{ opacity: 0.06 }}>
          <svg width="100%" height="100%">
            <defs>
              <pattern id="quote-pat" x="0" y="0" width="50" height="50" patternUnits="userSpaceOnUse">
                <polygon points="25,2 48,15 48,35 25,48 2,35 2,15" fill="none" stroke="white" strokeWidth="1"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#quote-pat)"/>
          </svg>
        </div>
        <div className="relative z-10 max-w-3xl mx-auto px-4 text-center">
          <p className="leading-loose mb-3" style={{ fontFamily: "'Amiri', serif", color: "#d4af37", fontSize: "clamp(1.5rem, 5vw, 3rem)" }}>
            {quoteArabic}
          </p>
          <p className="text-base sm:text-lg font-semibold text-white mb-1">{quoteTranslation}</p>
          <p className="text-sm" style={{ color: "#86efac" }}>{quoteSource}</p>
        </div>
      </section>

      {/* ===== ARTIKEL TERBARU ===== */}
      <section data-testid="articles-section" className="py-8 sm:py-12 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-end justify-between mb-5 sm:mb-7">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold tracking-widest mb-1.5"
                style={{ backgroundColor: "#eef9f0", color: "#198530", border: "1px solid #bbf7d0" }}>
                &#10022; INFORMASI TERKINI
              </div>
              <h2 className="text-xl sm:text-3xl font-bold text-gray-900">Artikel Terbaru</h2>
            </div>
            <Link to="/artikel" className="hidden md:flex items-center gap-2 font-semibold text-sm hover:underline" style={{ color: "#198530" }}>
              Lihat Semua <ArrowRight size={16} />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {articles.map((art, idx) => (
              <Link key={art.id || idx} to={`/artikel/${art.id}`} data-testid={`article-card-${idx}`}
                className="rounded-xl overflow-hidden transition-all duration-300 cursor-pointer bg-white block"
                style={{ border: "1px solid #f0f0f0", boxShadow: "0 2px 6px rgba(0,0,0,0.04)" }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = "0 10px 24px rgba(0,0,0,0.09)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 2px 6px rgba(0,0,0,0.04)"; }}
              >
                <div className="overflow-hidden relative" style={{ aspectRatio: "4/3" }}>
                  <img src={art.cover_image || "https://via.placeholder.com/400x300?text=Tozan"} alt={art.title}
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-110" loading="lazy" />
                  <div className="absolute top-2 left-2">
                    <span className="text-xs font-bold px-2 py-0.5 rounded-full"
                      style={{ backgroundColor: "rgba(26,122,46,0.9)", color: "white", fontSize: "0.6rem" }}>
                      {art.category}
                    </span>
                  </div>
                </div>
                <div className="p-2.5">
                  <h3 className="font-bold text-gray-900 mb-1 line-clamp-2 leading-snug" style={{ fontSize: "0.7rem" }}>{art.title}</h3>
                  <p className="text-gray-400" style={{ fontSize: "0.6rem" }}>
                    {art.created_at ? new Date(art.created_at).toLocaleDateString("id-ID", { day: "numeric", month: "short" }) : ""}
                  </p>
                </div>
              </Link>
            ))}
          </div>
          <div className="mt-4 flex justify-center md:hidden">
            <Link to="/artikel" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-bold text-sm"
              style={{ backgroundColor: "#eef9f0", color: "#198530", border: "1px solid #bbf7d0" }}>
              Lihat Semua <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </section>

      {/* ===== CTA DONASI ===== */}
      <section data-testid="donasi-section" className="py-8 sm:py-12 relative overflow-hidden" style={{ backgroundColor: "#044712", marginBottom: "3rem" }}>
        <div className="absolute inset-0" style={{ opacity: 0.07 }}>
          <svg width="100%" height="100%">
            <defs>
              <pattern id="donasi-pat" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse">
                <path d="M30 0L60 30L30 60L0 30Z" fill="none" stroke="white" strokeWidth="1"/>
                <circle cx="30" cy="30" r="12" fill="none" stroke="white" strokeWidth="0.8"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#donasi-pat)"/>
          </svg>
        </div>
        <div className="max-w-3xl mx-auto px-4 text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold tracking-widest mb-3"
            style={{ border: "1px solid rgba(212,175,55,0.4)", color: "#d4af37", backgroundColor: "rgba(212,175,55,0.08)" }}>
            &#10022; AMAL JARIYAH
          </div>
          <p className="leading-loose mb-2" style={{ fontFamily: "'Amiri', serif", color: "#d4af37", fontSize: "clamp(1.4rem, 4.5vw, 2.5rem)" }}>
            {"\u0645\u064e\u0639\u064b\u0627 \u0644\u0650\u0623\u064e\u062c\u0652\u064a\u064e\u0627\u0644\u064d \u0642\u064f\u0631\u0652\u0622\u0646\u0650\u064a\u0651\u064e\u0629\u064d"}
          </p>
          <h2 className="text-xl sm:text-3xl font-bold text-white mb-2">Mari Bersama Membangun Generasi Qur'ani</h2>
          <p className="mb-6 max-w-2xl mx-auto leading-relaxed text-sm" style={{ color: "#a7d4b3" }}>
            Dukungan Anda menjadi amal jariyah yang mengalir tanpa terputus.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href={waUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-donasi-button"
              className="inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3.5 sm:py-4 rounded-full font-bold text-sm sm:text-base transition-all duration-300 hover:scale-105 hover:shadow-2xl active:scale-95"
              style={{ backgroundColor: "#d4af37", color: "#044712" }}
            >
              <MessageCircle size={20} />
              Donasi via WhatsApp
            </a> 
          </div>
        </div>
      </section>
    </div>
  );
}
